import { Button, DropdownSelector, Label, Modal } from "@indigo-components";
import { Input } from "antd";
import { FC, useEffect, useState } from "react";
import {
  buildIndyFeedbackTicketUrl,
  INDY_FEEDBACK_CATEGORIES,
  IndyFeedbackCategory,
} from "~/utils/indyFeedbackTicketUrl";

/** The reply being reported. Null closes the modal. */
export interface IndyFeedbackTarget {
  conversationId: string;
  messageId: string | undefined;
  /** Plain markdown of the reply, as Copy would produce it. */
  assistantMessage: string;
}

interface IndyFeedbackModalProps {
  target: IndyFeedbackTarget | null;
  onClose: () => void;
}

const CATEGORY_OPTIONS = INDY_FEEDBACK_CATEGORIES.map((c) => ({
  value: c,
  label: c,
}));

/**
 * Thumbs-down modal for an assistant reply: pick an issue category, add
 * optional details, and open a pre-filled Linear ticket in a new tab — the
 * same hand-off `FileMrrTicketModal` uses for MRR entries.
 */
export const IndyFeedbackModal: FC<IndyFeedbackModalProps> = ({
  target,
  onClose,
}) => {
  const [category, setCategory] = useState<IndyFeedbackCategory | null>(null);
  const [feedback, setFeedback] = useState("");

  // Fresh form for each reported reply.
  useEffect(() => {
    if (target) {
      setCategory(null);
      setFeedback("");
    }
  }, [target]);

  const handleSubmit = () => {
    if (!target || !category) return;
    const url = buildIndyFeedbackTicketUrl({
      category,
      feedback,
      conversationId: target.conversationId,
      messageId: target.messageId,
      assistantMessage: target.assistantMessage,
    });
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <Modal
      open={target !== null}
      onCancel={onClose}
      footer={null}
      centered
      width={560}
      destroyOnHidden
      title={
        <Label size="lg" weight="semibold">
          What could have been better?
        </Label>
      }
    >
      <div className="flex flex-col gap-4">
        <DropdownSelector
          label="Issue"
          options={CATEGORY_OPTIONS}
          value={category}
          onChange={(v: IndyFeedbackCategory | null) => setCategory(v)}
          variant="filled"
          width="full"
          placeholder="Select an issue"
        />

        <div className="flex flex-col gap-1.5">
          <Label size="sm" weight="medium">
            Details
          </Label>
          <Input.TextArea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Share details about what went wrong…"
            autoSize={{ minRows: 4, maxRows: 10 }}
            maxLength={2000}
          />
        </div>

        <div className="flex justify-end items-center gap-2 pt-2">
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={category === null}
          >
            Open in Linear
          </Button>
        </div>
      </div>
    </Modal>
  );
};
